import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Fade } from 'react-awesome-reveal';
import KidsCodePageHero from './KidsCodePageHero';
import kd03 from '../Images/kd03.jpg';
import { useNavigate } from 'react-router-dom';
import ProgramPricing from './ProgramPricing';

// Fun pulse animation for badges and highlights
const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.03); }
`;

const PageContainer = styled.div`
  background: #fdfbf7; /* Warm, premium off-white background */
  color: #1f2937;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  overflow-x: hidden;
  padding: 0;
  margin: 0;
  width: 100%;
  margin-top: 50px;
`;

const Section = styled.section`
  padding: 10px;
  margin: 10px auto;
  max-width: 1200px;
`;

const HeroSection = styled(Section)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  align-items: center;
  padding: 15px 10px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const HeroContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Badge = styled.span`
  background: #ffedd5;
  color: #c2410c;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 700;
  border-radius: 20px;
  width: fit-content;
  border: 1px solid #fed7aa;
  animation: ${pulse} 3s ease-in-out infinite;

  @media (max-width: 768px) {
    margin: 0 auto;
  }
`;

const Title = styled.h1`
  font-size: 34px;
  color: #0f172a;
  margin: 0;
  line-height: 1.15;
  font-weight: 900;
  word-break: break-word;
`;

const Subtitle = styled.p`
  font-size: 15px;
  color: #4b5563;
  margin: 0;
  line-height: 1.4;
  word-break: break-word;
`;

const Button = styled.button`
  background: linear-gradient(135deg, #f97316, #ea580c);
  color: #ffffff;
  border: none;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 700;
  border-radius: 8px;
  cursor: pointer;
  width: fit-content;
  box-shadow: 0 4px 12px rgba(249, 115, 22, 0.25);
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(249, 115, 22, 0.35);
  }

  @media (max-width: 768px) {
    margin: 0 auto;
  }
`;

const StyledImage = styled.img`
  width: 100%;
  height: 240px;
  object-fit: cover;
  border-radius: 12px;
  border: 3px solid #ffffff;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
`;

const TimelineGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 10px;
  margin: 10px 0;
`;

const FeatureCard = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  padding: 12px;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-3px);
    border-color: #f97316;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: #f97316;
  }
`;

const CardTitle = styled.h3`
  font-size: 16px;
  color: #1f2937;
  margin: 0;
  font-weight: 700;
`;

const CardText = styled.p`
  font-size: 13px;
  color: #6b7280;
  margin: 0;
  line-height: 1.35;
  word-break: break-word;
`;

const SectionHeader = styled.h2`
  font-size: 24px;
  color: #0f172a;
  text-align: center;
  margin: 15px 0 10px 0;
  font-weight: 800;
  letter-spacing: -0.3px;
`;

const CalloutBox = styled(Section)`
  background: linear-gradient(135deg, #1e1b4b, #312e81);
  color: #ffffff;
  text-align: center;
  padding: 20px 10px;
  border-radius: 12px;
  margin: 15px auto;
  box-shadow: 0 10px 30px rgba(49, 46, 129, 0.2);

  ${Title} {
    color: #ffffff;
  }

  ${Subtitle} {
    color: #e2e8f0;
    margin: 0 auto 10px auto;
  }

  ${Button} {
    background: #f97316;
    margin: 0 auto;
    display: block;
  }
`;

export default function WeekendCodingAndAIPage() {
    const navigate=useNavigate();
  return (
    <PageContainer>
      <KidsCodePageHero />
      
      {/* Hero Section with Split Layout */}
      <Fade triggerOnce>
        <HeroSection>
          <HeroContent>
            <Badge>⚡ WEEKEND TECH ADVENTURES</Badge>
            <Title>Weekend Coding & AI Classes for Kids & Teens</Title>
            <Subtitle>
              Turn weekends into an exciting journey of creation! Keep young minds consistently sharp with hands-on projects in coding, robotics, and artificial intelligence. Ages 6–16.
            </Subtitle>
            <Button onClick={()=>navigate('/codingapplicationfaq')}>Enroll for Weekend Classes</Button>
          </HeroContent>
          <div>
            <StyledImage src={kd03} alt="Weekend coding and AI classes kids" />
          </div>
        </HeroSection>
      </Fade>

      {/* What Kids Learn - Feature Cards with Accent Borders */}
      <Fade triggerOnce>
        <Section>
          <SectionHeader>What Kids Master Every Weekend</SectionHeader>
          <TimelineGrid>
            <FeatureCard>
              <CardTitle>🚀 Coding & Game Logic</CardTitle>
              <CardText>Build robust programming foundations through interactive weekly mini-projects, animations, and custom game loops.</CardText>
            </FeatureCard>
            <FeatureCard style={{ '&::before': { background: '#06b6d4' } }}>
              <CardTitle>🤖 AI & Smart Prompts</CardTitle>
              <CardText>Introduce kids to machine learning fundamentals, smart helper assistants, and innovative prompt engineering techniques.</CardText>
            </FeatureCard>
            <FeatureCard style={{ '&::before': { background: '#10b981' } }}>
              <CardTitle>⚙️ Robotics & Hardware</CardTitle>
              <CardText>Combine digital logic code with physical computing concepts to design responsive automated gadgets.</CardText>
            </FeatureCard>
          </TimelineGrid>
        </Section>
      </Fade>

      {/* Program Highlights */}
      <Fade triggerOnce>
        <Section>
          <SectionHeader>Why Choose Weekend Cohorts?</SectionHeader>
          <TimelineGrid>
            <FeatureCard>
              <CardTitle>Consistent Habit Growth</CardTitle>
              <CardText>Weekly intervals ensure long-term skill retention and steady technical progress without school friction.</CardText>
            </FeatureCard>
            <FeatureCard>
              <CardTitle>Hybrid Participation</CardTitle>
              <CardText>Join through vibrant physical weekend classrooms or connect seamlessly via live instructor-led video sessions.</CardText>
            </FeatureCard>
            <FeatureCard>
              <CardTitle>Monthly Mini-Showcase</CardTitle>
              <CardText>Every month wraps up with an interactive review session where kids proudly demonstrate their newest creations.</CardText>
            </FeatureCard>
          </TimelineGrid>
        </Section>
      </Fade>

      {/* Dark Styled Callout Box */}
      <Fade triggerOnce>
        <CalloutBox>
          <Title style={{ fontSize: '24px', marginBottom: '8px' }}>Build the Future, One Weekend at a Time</Title>
          <Subtitle style={{ maxWidth: '600px' }}>
            Class sizes are strictly limited to guarantee personalized mentorship from our expert instructors. Secure your child's weekend seat today!
          </Subtitle>
          <Button onClick={()=>navigate('/codingapplicationfaq')}>Register for Weekend Classes</Button>
        </CalloutBox>
      </Fade>
      <ProgramPricing/>
    </PageContainer>
  );
}